import type { AdminOrderResponse, AdminProposalHistory } from '@inflip/contracts/admin';
import type { ContractTerms } from '@inflip/contracts/user';
import { useId, useState } from 'react';

import { orderContractRows } from './order-contract-terms';
import { Button, InfoRow, Section } from './ui';
import { formatDateTime } from '../lib/time-zone';

export function proposalChanges(before: ContractTerms, after: ContractTerms) {
  const previous = new Map(orderContractRows(before).map((row) => [row.label, row.value]));
  const current = new Map(orderContractRows(after).map((row) => [row.label, row.value]));
  return [...new Set([...previous.keys(), ...current.keys()])]
    .filter((label) => previous.get(label) !== current.get(label))
    .map((label) => ({
      label,
      before: previous.get(label) ?? '없음',
      after: current.get(label) ?? '없음',
    }));
}

export function OrderProposalHistory({ order }: { order: AdminOrderResponse }) {
  const [expanded, setExpanded] = useState(false);
  const historyId = useId();
  const history = order.proposalHistory;
  if (!history) return null;
  return (
    <Section
      title={`협의 이력 · 제안 ${history.rounds.length}회`}
      action={
        <Button
          variant="secondary"
          size="sm"
          aria-expanded={expanded}
          aria-controls={historyId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '접기' : '협의 이력 보기'}
        </Button>
      }
    >
      <div id={historyId} hidden={!expanded}>
        <p className="pb-3 text-xs text-muted-foreground">
          주문 생성 전 제안 기록입니다. 시각은 한국 시간 기준입니다.
        </p>
        <ProposalRounds
          history={history}
          buyer={{ id: order.buyerId, name: order.buyerName }}
          seller={{ id: order.sellerId, name: order.sellerName }}
        />
      </div>
    </Section>
  );
}

/** 협의 차수별 제안 — 주문 상세 협의 이력과 조건 협의 목록이 같이 쓴다 */
export function ProposalRounds({
  history,
  buyer,
  seller,
}: {
  history: AdminProposalHistory;
  buyer: { id: string; name: string | null };
  seller: { id: string | null; name: string | null };
}) {
  const actor = (id: string | null) => {
    if (id === buyer.id) return `구매자${buyer.name ? ` · ${buyer.name}` : ''}`;
    if (id && id === seller.id) return `판매자${seller.name ? ` · ${seller.name}` : ''}`;
    return id ? `참여자 · ${id}` : '제안자 기록 없음';
  };
  const time = (at: string | null) => (at ? formatDateTime(at) : '시각 기록 없음');
  return (
    <>
      <ol className="divide-y divide-border/60">
        {history.rounds.map((entry, index) => {
          const previous = history.rounds[index - 1];
          const changes =
            previous?.terms && entry.terms ? proposalChanges(previous.terms, entry.terms) : null;
          return (
            <li key={entry.round} className="space-y-3 py-4 text-sm">
              <p className="font-semibold">
                {entry.round}차 제안 · {actor(entry.proposedBy)}
              </p>
              <p className="text-xs text-muted-foreground">{time(entry.proposedAt)}</p>
              {entry.message && <p className="whitespace-pre-wrap break-words">{entry.message}</p>}
              {entry.terms ? (
                <>
                  <InfoRow label="총 제안 금액">
                    <span className="font-bold tabular-nums">
                      {entry.terms.upfrontAmount.toLocaleString('ko-KR')}원
                    </span>
                  </InfoRow>
                  {changes &&
                    (changes.length ? (
                      <div className="divide-y divide-border/60">
                        {changes.map((change) => (
                          <InfoRow key={change.label} label={change.label}>
                            <span className="whitespace-pre-wrap break-words text-muted-foreground">
                              이전: {change.before}
                              <br />
                              변경: <span className="text-foreground">{change.after}</span>
                            </span>
                          </InfoRow>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">금액·조건 변경 없음</p>
                    ))}
                  {previous && !previous.terms && (
                    <p className="text-muted-foreground">
                      이전 조건 기록이 없어 비교할 수 없습니다.
                    </p>
                  )}
                  <ProposalTermsDisclosure terms={entry.terms} />
                </>
              ) : (
                <p className="text-muted-foreground">조건 기록을 확인할 수 없습니다.</p>
              )}
            </li>
          );
        })}
      </ol>
      {history.acceptedBy && (
        <div className="border-t border-border/60 py-4 text-sm">
          <p className="font-semibold">최종 수락 · {actor(history.acceptedBy)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{time(history.acceptedAt)}</p>
        </div>
      )}
    </>
  );
}

function ProposalTermsDisclosure({ terms }: { terms: ContractTerms }) {
  const [expanded, setExpanded] = useState(false);
  const termsId = useId();
  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={expanded}
        aria-controls={termsId}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '전체 조건 접기' : '이 제안의 전체 조건 보기'}
      </Button>
      <div id={termsId} hidden={!expanded} className="mt-2 divide-y divide-border/60">
        {orderContractRows(terms).map((row, i) => (
          <InfoRow key={`${i}-${row.label}`} label={row.label}>
            <span className="whitespace-pre-wrap break-words">{row.value}</span>
          </InfoRow>
        ))}
      </div>
    </div>
  );
}
